import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  useDisclosure,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Badge,
  Image,
  Text,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { DataTable } from 'components';
import { Api } from 'api';
import { EStoreProduct, ExpertisePointGroupType, ExpertisePointGroupTypeLabel } from 'types';
import { AdminPageTitle, AdminProductModal } from '../components';

interface AdminProductTableData {
  id: number;
  name: string;
  title: string;
  price: number;
  photoUrls: string[];
  isActive: boolean;
  groupType?: ExpertisePointGroupType;
  variants?: any[];
  description?: string;
}

export const AdminEStoreManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  // Dark mode colors
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const textTertiary = useColorModeValue('gray.500', 'gray.400');
  const searchIconColor = useColorModeValue('gray.300', 'gray.500');

  // State for filters
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal states
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const { isOpen: isProductModalOpen, onOpen: onProductModalOpen, onClose: onProductModalClose } = useDisclosure();
  const [selectedProduct, setSelectedProduct] = useState<AdminProductTableData | null>(null);
  const [editingProduct, setEditingProduct] = useState<AdminProductTableData | null>(null);
  const deleteDialogCancelRef = React.useRef(null);

  // Fetch products
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsError,
  } = useQuery<EStoreProduct[]>(
    ['adminEStoreProducts', searchQuery, brandFilter, statusFilter],
    async () => {
      const params = new URLSearchParams();

      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (brandFilter !== 'all') {
        params.append('groupType', brandFilter);
      }
      if (statusFilter !== 'all') {
        params.append('isActive', statusFilter === 'active' ? 'true' : 'false');
      }

      const response = await Api.Product.searchProducts(params.toString());
      return response.data || [];
    },
    {
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Error fetching products:', error);
        toast({
          title: 'Hata',
          description: 'Ürünler yüklenirken bir sorun oluştu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      },
    },
  );

  // Transform data for table (no need for frontend filtering since backend handles it)
  const tableData: AdminProductTableData[] = useMemo(() => {
    if (!productsData) return [];

    return productsData.map((product) => ({
      id: product.id,
      name: product.name,
      title: product.title || '',
      price: product.price,
      photoUrls: product.photoUrls || [],
      isActive: product.isActive,
      groupType: product.groupType,
      variants: product.variants,
      description: product.description,
    }));
  }, [productsData]);

  // Delete mutation
  const deleteMutation = useMutation(
    async (productId: number) => {
      await Api.Product.removeProduct(productId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminEStoreProducts');
        toast({
          title: 'Başarılı',
          description: 'Ürün başarıyla silindi.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteDialogClose();
      },
      onError: (error) => {
        console.error('Error deleting product:', error);
        toast({
          title: 'Hata',
          description: 'Ürün silinirken bir hata oluştu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      },
    },
  );

  // Handle delete
  const handleDeleteProduct = (product: AdminProductTableData) => {
    setSelectedProduct(product);
    onDeleteDialogOpen();
  };

  const confirmDelete = () => {
    if (selectedProduct) {
      deleteMutation.mutate(selectedProduct.id);
    }
  };

  // Handle edit
  const handleEditProduct = (product: AdminProductTableData) => {
    setEditingProduct(product);
    onProductModalOpen();
  };

  // Handle add new product
  const handleAddProduct = () => {
    setEditingProduct(null);
    onProductModalOpen();
  };

  // Handle modal success
  const handleModalSuccess = () => {
    setEditingProduct(null);
  };

  // Table columns
  const columnHelper = createColumnHelper<AdminProductTableData>();

  const columns: ColumnDef<AdminProductTableData, any>[] = [
    columnHelper.accessor('photoUrls', {
      header: 'Görsel',
      cell: ({ getValue }) => {
        const photoUrls = getValue();
        const imageUrl = photoUrls?.[0] || 'https://placehold.co/60x60?text=No+Image';
        return (
          <Image
            src={imageUrl}
            alt="Product"
            boxSize="60px"
            objectFit="cover"
            borderRadius="md"
            fallbackSrc="https://placehold.co/60x60?text=No+Image"
          />
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor('name', {
      header: 'Ürün Adı',
      cell: ({ row }) => (
        <Box>
          <Text fontWeight="bold" fontSize="sm">
            {row.original.name}
          </Text>
          <Text fontSize="xs" color={textSecondary}>
            {row.original.title}
          </Text>
        </Box>
      ),
    }),
    columnHelper.accessor('price', {
      header: 'Fiyat',
      cell: ({ getValue }) => {
        const price = getValue();
        return (
          <Text fontWeight="semibold">
            {price != null ? price.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) : '-'}
          </Text>
        );
      },
    }),
    columnHelper.accessor('groupType', {
      header: 'Marka',
      cell: ({ getValue }) => {
        const groupType = getValue();
        if (!groupType) return <Text>-</Text>;
        const label = ExpertisePointGroupTypeLabel.get(groupType) || 'Bilinmiyor';
        return (
          <Badge colorScheme={groupType === ExpertisePointGroupType.OccoExpertise ? 'blue' : 'green'}>{label}</Badge>
        );
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum',
      cell: ({ getValue }) => (
        <Badge colorScheme={getValue() ? 'green' : 'red'}>{getValue() ? 'Aktif' : 'Pasif'}</Badge>
      ),
    }),
    columnHelper.display({
      id: 'variants',
      header: 'Varyantlar',
      cell: ({ row }) => {
        const { variants } = row.original;
        if (!variants || variants.length === 0) {
          return (
            <Text fontSize="sm" color={textTertiary}>
              Yok
            </Text>
          );
        }
        return (
          <Badge variant="outline" colorScheme="blue">
            {variants.length} Varyant
          </Badge>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'İşlemler',
      cell: ({ row }) => (
        <HStack spacing={1}>
          <Tooltip label="Düzenle">
            <IconButton
              aria-label="Düzenle"
              icon={<EditIcon />}
              variant="ghost"
              size="sm"
              colorScheme="blue"
              onClick={() => handleEditProduct(row.original)}
            />
          </Tooltip>
          <Tooltip label="Sil">
            <IconButton
              aria-label="Sil"
              icon={<DeleteIcon />}
              variant="ghost"
              size="sm"
              colorScheme="red"
              onClick={() => handleDeleteProduct(row.original)}
            />
          </Tooltip>
        </HStack>
      ),
      enableSorting: false,
    }),
  ];

  if (productsError) {
    return (
      <Box p={6}>
        <AdminPageTitle title="E-Mağaza Yönetimi" />
        <Alert status="error">
          <AlertIcon />
          Ürünler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <AdminPageTitle title="E-Mağaza Yönetimi" />

      {/* Filters and Search */}
      <HStack spacing={4} mb={6} flexWrap="wrap">
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={searchIconColor} />
          </InputLeftElement>
          <Input
            placeholder="Ürün adı veya başlık ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>

        <Select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} maxW="200px">
          <option value="all">Tüm Markalar</option>
          {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
            <option key={key} value={key.toString()}>
              {value}
            </option>
          ))}
        </Select>

        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} maxW="150px">
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="inactive">Pasif</option>
        </Select>

        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleAddProduct}>
          Yeni Ürün
        </Button>
      </HStack>

      {/* Data Table */}
      {isLoadingProducts ? (
        <Center py={10}>
          <Spinner size="lg" />
        </Center>
      ) : (
        <DataTable data={tableData} columns={columns} />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={deleteDialogCancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Ürünü Sil
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text>
                <strong>{selectedProduct?.name}</strong> adlı ürünü silmek istediğinizden emin misiniz?
              </Text>
              <Text mt={2} fontSize="sm" color={textSecondary}>
                Bu işlem geri alınamaz.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={deleteDialogCancelRef} onClick={onDeleteDialogClose}>
                İptal
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3} isLoading={deleteMutation.isLoading}>
                Sil
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Product Modal */}
      <AdminProductModal
        isOpen={isProductModalOpen}
        onClose={onProductModalClose}
        product={editingProduct}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
};
